import React, { useCallback, useMemo, useState } from "react"

import {
  buildMetricGroups,
  getMetricGroupIcon,
  GROUPING_OPTIONS,
  METRICS_GROUPING_OPTION,
  METRICS_SORT_OPTION,
  type GroupingOption,
  type MetricGroup,
} from "@/metrics/grouping"
import { resolveMetricCategory, useMetricDirectory } from "@/metrics/registry"
import {
  Alert,
  cn,
  SearchInput,
  SelectionChip,
  type SortDescriptor,
} from "@coval/ui"
import { Bot, type LucideIcon } from "lucide-react"

import { GroupingDisplay } from "@/components/filters/GroupingDisplay"
import { BrowseTable as Table } from "@/components/tables/BrowseTable"
import {
  TableDisplayScope,
  TableDisplaySlot,
} from "@/components/tables/TableDisplayScope"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import {
  buildMetricToggleActions,
  buildSelectAllMetricActions,
  hasReachedMetricSelectionLimit,
} from "../utils/metricSelectionLimit"
import { MetricAvailabilityNotice } from "./MetricAvailabilityNotice"
import type { MetricCatalogItem } from "./metricCatalogItem"
import {
  buildMetricPickerColumns,
  type MetricPickerRowNode,
} from "./MetricPickerColumns"

const TRACE_CATEGORY = "Trace"

// The agent's own metrics lead the grouped sections. Its key is prefixed so it
// can never collide with a category, type, or tag key.
const AGENT_SECTION_KEY = "__agent__"

// Row-height estimates for the virtualizer. These mirror MetricsTableView's, and
// the two gap constants must stay in sync with the Table's own platter padding
// (CARD_ROW_SECTION_GAP / CARD_ROW_HEADER_GAP).
const METRIC_ROW_HEIGHT = 56
const METRIC_GROUP_ROW_HEIGHT = 26
const METRIC_SECTION_GAP = 36
const METRIC_HEADER_CONTENT_GAP = 14
const METRIC_COLUMN_HEADER_ROW_HEIGHT = 40

export interface MetricTableProps {
  className?: string
  metrics: MetricCatalogItem[]
  selectedMetrics: string[]
  selectionKey?: "name" | "id"
  additionsDisabled?: boolean
  selectionCount?: number
  agentMetricsIds?: string[]
  onToggleMetric: (metricName: string, isSelected: boolean) => void
  instanceId: string
  allowMultipleSelection?: boolean
  maxSelections?: number
  metricErrors?: Record<string, string>
  tagColorMap?: Record<string, string | null | undefined>
  /**
   * Heading for the catalog, rendered at the leading edge of the controls row.
   * A dialog hands its own title down instead of stacking a header above, so
   * the search and the grouping controls sit beside the heading rather than in
   * a band under it. Without one the controls row keeps the full width and the
   * search takes the slack.
   */
  title?: React.ReactNode
  /** Rendered between the controls and the table, for a rule the rows can't state. */
  banner?: React.ReactNode
}

const iconClasses = "h-4 w-4 shrink-0 text-coval-text-muted"

export function MetricTable({
  className = "h-[70vh]",
  metrics,
  selectedMetrics,
  selectionKey = "name",
  additionsDisabled = false,
  selectionCount,
  agentMetricsIds,
  onToggleMetric,
  instanceId,
  allowMultipleSelection = true,
  maxSelections,
  metricErrors = {},
  tagColorMap,
  title,
  banner,
}: MetricTableProps) {
  const getSelectionKey = useCallback(
    (metric: MetricCatalogItem) =>
      selectionKey === "id"
        ? (metric.id ?? metric.metric_name)
        : metric.metric_name,
    [selectionKey],
  )
  const [searchQuery, setSearchQuery] = useState("")
  const [grouping, setGrouping] = useState<GroupingOption>(
    METRICS_GROUPING_OPTION.CATEGORY,
  )
  // null = every section expanded; becomes a concrete list once the user toggles
  // a section or uses expand/collapse-all.
  const [expandedGroups, setExpandedGroups] = useState<string[] | null>(null)
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    columnId: METRICS_SORT_OPTION.NAME,
    direction: "asc",
  })
  const { byManager } = useMetricDirectory()

  const getMetricType = useCallback(
    (metric: MetricCatalogItem): string =>
      resolveMetricCategory(
        byManager[metric.metric_manager ?? ""],
        metric.metric_manager,
      ),
    [byManager],
  )

  const filteredMetrics = useMemo(() => {
    const trimmedQuery = searchQuery.trim()
    if (!trimmedQuery) {
      return metrics
    }

    const matchesTag = (m: MetricCatalogItem, query: string) =>
      (m.tags ?? []).some((tag) => tag.toLowerCase().includes(query))

    // `tag:<value>` filters strictly by tag (e.g. "tag:billing").
    const tagFilterMatch = trimmedQuery.match(/^tag:(.*)$/i)
    if (tagFilterMatch) {
      const tagQuery = tagFilterMatch[1].trim().toLowerCase()
      if (!tagQuery) {
        return metrics
      }
      return metrics.filter((m) => matchesTag(m, tagQuery))
    }

    const normalizedQuery = trimmedQuery.toLowerCase()

    // Search by metric name, display name, description, category, or tag.
    return metrics.filter(
      (m) =>
        (m.metric_display_name || "").toLowerCase().includes(normalizedQuery) ||
        (m.metric_name || "").toLowerCase().includes(normalizedQuery) ||
        (m.description || "").toLowerCase().includes(normalizedQuery) ||
        getMetricType(m).toLowerCase().includes(normalizedQuery) ||
        matchesTag(m, normalizedQuery),
    )
  }, [metrics, searchQuery, getMetricType])

  const selectedNames = useMemo(
    () => new Set(selectedMetrics),
    [selectedMetrics],
  )

  // The agent's own metrics are pinned above the grouped sections, and a metric
  // can appear in more than one section (under each of its tags, say) —
  // selection is keyed by metric name, so every copy of a row reads the same.
  const sections = useMemo(() => {
    const pinned: Array<{
      group: MetricGroup<MetricCatalogItem>
      icon: LucideIcon
    }> = []

    const agentMetricIdSet = new Set(agentMetricsIds ?? [])
    const agentMetrics = agentMetricIdSet.size
      ? filteredMetrics.filter(
          (metric) => metric.id && agentMetricIdSet.has(metric.id),
        )
      : []
    if (agentMetrics.length > 0) {
      pinned.push({
        group: {
          key: AGENT_SECTION_KEY,
          label: "Agent Metrics",
          count: agentMetrics.length,
          description: "Already linked to this agent",
          metrics: agentMetrics,
        },
        icon: Bot,
      })
    }

    const grouped = buildMetricGroups({
      metrics: filteredMetrics,
      grouping,
      byManager,
      sortDescriptor,
    }).map((group) => ({
      group,
      icon: getMetricGroupIcon(group, grouping),
    }))

    return [...pinned, ...grouped]
  }, [agentMetricsIds, byManager, filteredMetrics, grouping, sortDescriptor])

  const data = useMemo<MetricPickerRowNode[]>(
    () =>
      sections.map(({ group, icon }) => ({
        kind: "group" as const,
        id: group.key,
        group,
        icon,
        // An empty section shows its title alone rather than a platter of bare
        // column headers.
        children: group.metrics.length
          ? [
              { kind: "columnHeader" as const, id: `${group.key}:__columns__` },
              ...group.metrics.map((metric) => ({
                kind: "metric" as const,
                id: `${group.key}:${getSelectionKey(metric)}`,
                metric,
              })),
            ]
          : [],
      })),
    [sections, getSelectionKey],
  )

  const sectionKeys = useMemo(
    () => sections.map(({ group }) => group.key),
    [sections],
  )

  // Every section is expanded until the user says otherwise, so a metric is
  // never hidden behind a collapsed band the moment the dialog opens.
  const expandedIds = expandedGroups ?? sectionKeys

  // Compared by key rather than by count: a pinned list can hold keys that no
  // longer name a section — a search narrowing the sections, say — and a bare
  // length check would then read "all expanded" while nothing is.
  const allExpanded = useMemo(() => {
    const expanded = new Set(expandedIds)
    return (
      sectionKeys.length > 0 && sectionKeys.every((key) => expanded.has(key))
    )
  }, [expandedIds, sectionKeys])

  const handleToggleExpandAll = () => {
    setExpandedGroups(allExpanded ? [] : sectionKeys)
  }

  // Section keys are grouping-specific — Category yields `audio-fidelity`, Tag
  // yields `tag-billing` — so a list pinned under the old grouping matches
  // nothing under the new one and would leave every band collapsed. Drop it and
  // fall back to expanding everything.
  const handleGroupingChange = (next: GroupingOption) => {
    setGrouping(next)
    setExpandedGroups(null)
  }

  // Unavailable IDs remain verbatim so removal can be matched to the stored relation.
  const selectedChips = useMemo(() => {
    const byName = new Map(
      metrics.map((metric) => [getSelectionKey(metric), metric] as const),
    )
    return selectedMetrics.map((metricName) => ({
      metricName,
      label:
        selectionKey === "id" && !byName.has(metricName)
          ? metricName
          : resolveMetricDisplayName(
              byName.get(metricName)?.metric_display_name,
              metricName,
            ),
    }))
  }, [metrics, selectedMetrics, getSelectionKey, selectionKey])

  const resolvedSelectionCount = selectionCount ?? selectedMetrics.length
  const isSelectionLimitReached = hasReachedMetricSelectionLimit(
    resolvedSelectionCount,
    maxSelections,
  )

  // Blocking additions keeps selected rows and chips removable.
  const isMetricAdditionBlocked = useCallback(
    (metric: MetricCatalogItem) =>
      (additionsDisabled ||
        (allowMultipleSelection && isSelectionLimitReached)) &&
      !selectedNames.has(getSelectionKey(metric)),
    [
      additionsDisabled,
      allowMultipleSelection,
      isSelectionLimitReached,
      selectedNames,
      getSelectionKey,
    ],
  )

  // A metric whose config is incompatible can never be picked, cap or not.
  const isMetricSelectable = useCallback(
    (metric: MetricCatalogItem) =>
      !metricErrors[metric.metric_name] && !isMetricAdditionBlocked(metric),
    [isMetricAdditionBlocked, metricErrors],
  )

  const applyActions = useCallback(
    (actions: Array<{ metricName: string; isSelected: boolean }>) => {
      actions.forEach(({ metricName, isSelected }) => {
        if (!isSelected || !additionsDisabled) {
          onToggleMetric(metricName, isSelected)
        }
      })
    },
    [onToggleMetric, additionsDisabled],
  )

  const handleMetricToggle = useCallback(
    (metricName: string, isSelected: boolean) => {
      applyActions(
        buildMetricToggleActions(
          metricName,
          isSelected,
          selectedMetrics,
          allowMultipleSelection,
          maxSelections,
          selectionCount,
        ),
      )
    },
    [
      allowMultipleSelection,
      applyActions,
      maxSelections,
      selectedMetrics,
      selectionCount,
    ],
  )

  // Selection travels as row ids, but a metric can occupy several rows (its own
  // section plus "Selected Metrics"), so both directions translate through the
  // selection key: every row of a selected metric reads as checked, and a change
  // is read as the set of keys whose state actually flipped.
  //
  // A metric already in the selection can still be one this setup rules out — a
  // template picked on a voice run, reopened on a text one. Its row is withheld
  // from the selection so it doesn't wear the selected surface with no checkbox
  // to explain it; the chip above the table is what remains to remove it by.
  const selectedRowIds = useMemo(() => {
    const ids = new Set<string>()
    data.forEach((section) => {
      if (section.kind !== "group") {
        return
      }
      section.children.forEach((row) => {
        if (
          row.kind === "metric" &&
          selectedNames.has(getSelectionKey(row.metric)) &&
          !metricErrors[row.metric.metric_name]
        ) {
          ids.add(row.id)
        }
      })
    })
    return ids
  }, [data, metricErrors, selectedNames, getSelectionKey])

  const handleSelectionChange = useCallback(
    (nextIds: Set<string>) => {
      const additions: string[] = []
      const removals: string[] = []
      const seen = new Set<string>()

      data.forEach((section) => {
        if (section.kind !== "group") {
          return
        }
        section.children.forEach((row) => {
          // An unavailable row is never in `selectedRowIds`, so an incoming set
          // that omits it says nothing about the metric — reading it as a
          // removal would drop a metric the run already carries on the next
          // unrelated click.
          if (row.kind !== "metric" || metricErrors[row.metric.metric_name]) {
            return
          }
          const metricName = getSelectionKey(row.metric)
          const nextSelected = nextIds.has(row.id)
          if (nextSelected === selectedNames.has(metricName)) {
            return
          }
          if (seen.has(metricName)) {
            return
          }
          seen.add(metricName)
          ;(nextSelected ? additions : removals).push(metricName)
        })
      })

      applyActions(
        removals.map((metricName) => ({ metricName, isSelected: false })),
      )

      if (additions.length === 0) {
        return
      }
      if (!allowMultipleSelection) {
        // Single-select: the last row the change touched wins, and the util
        // clears whatever was selected before it.
        handleMetricToggle(additions[additions.length - 1], true)
        return
      }
      // Honors the selection limit, so a section's "select all" stops at the cap.
      applyActions(
        buildSelectAllMetricActions(
          additions,
          selectedMetrics,
          maxSelections,
          selectionCount,
        ),
      )
    },
    [
      allowMultipleSelection,
      applyActions,
      data,
      handleMetricToggle,
      getSelectionKey,
      maxSelections,
      metricErrors,
      selectedMetrics,
      selectedNames,
      selectionCount,
    ],
  )

  const [columnVisibilityOverrides, setColumnVisibilityOverrides] =
    React.useState<Record<string, boolean>>({})
  const columnVisibility: Record<string, boolean> = {
    type: grouping !== METRICS_GROUPING_OPTION.TYPE,
    category: grouping === METRICS_GROUPING_OPTION.TYPE,
    ...columnVisibilityOverrides,
  }
  const handleColumnVisibilityChange = (next: Record<string, boolean>) => {
    setColumnVisibilityOverrides((previous) => ({
      ...previous,
      ...Object.fromEntries(
        Object.entries(next).filter(
          ([key, visible]) => visible !== (columnVisibility[key] ?? true),
        ),
      ),
    }))
  }

  const columns = useMemo(
    () =>
      buildMetricPickerColumns({
        byManager,
        getMetricType,
        metricErrors,
        showTypeColumn: true,
        tagColorMap,
      }),
    [byManager, getMetricType, metricErrors, tagColorMap],
  )

  // Trace metrics only score when the agent sends OpenTelemetry traces. The
  // notice is tied to the selection rather than to the catalog: every org has
  // Trace metrics on offer, so warning about their presence would mean warning
  // on every open.
  const hasSelectedTraceMetric = useMemo(
    () =>
      metrics.some(
        (metric) =>
          selectedNames.has(getSelectionKey(metric)) &&
          getMetricType(metric) === TRACE_CATEGORY,
      ),
    [getMetricType, metrics, selectedNames, getSelectionKey],
  )

  return (
    <TableDisplayScope
      search={searchQuery}
      // The table below is `edgeToEdge` inside a `px-6` scroll container, so
      // the inset the columns actually start at is that `p-6`, not the table's
      // own gutter. Declaring it here is what `table-pad-x` reads, so the
      // header, banner and notice line up with the platters.
      className={`flex min-h-0 w-full flex-col [--table-edge-gutter:1.5rem] ${className}`}
    >
      <div
        className={cn(
          "table-pad-x border-b border-coval-border",
          // A hosted title carries the padding its own header used to, the same
          // above and below so it sits square between the dialog's top edge and
          // the rule under the row.
          title ? "py-5" : "pb-5",
        )}
      >
        <div className="flex items-center gap-2">
          {title ? <div className="mr-auto min-w-0">{title}</div> : null}
          <SearchInput
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onClear={() => setSearchQuery("")}
            placeholder={
              title
                ? "Search metrics (try tag:billing)..."
                : "Search by name, description, category, or tag (try tag:billing)..."
            }
            aria-label="Search metrics"
            data-testid={`${instanceId}-metric-search`}
            // Beside a title the field takes a fixed width rather than every
            // pixel the row has spare.
            className={title ? "w-72 shrink-0" : "flex-1"}
          />
          <TableDisplaySlot />
        </div>

        {selectedChips.length > 0 ? (
          <div className="mt-3 flex max-h-24 flex-wrap items-center gap-2 overflow-y-auto">
            {selectedChips.map(({ metricName, label }) => (
              <SelectionChip
                key={metricName}
                label={label}
                onRemove={() => handleMetricToggle(metricName, false)}
              />
            ))}
          </div>
        ) : null}
      </div>

      {banner ? <div className="table-pad-x pt-3">{banner}</div> : null}

      <MetricAvailabilityNotice metrics={metrics} metricErrors={metricErrors} />

      {hasSelectedTraceMetric ? (
        <div className="table-pad bg-surface-contrast">
          <Alert variant="warning">
            <span>
              <strong className="font-medium">
                You selected a Trace metric.
              </strong>{" "}
              Trace metrics only produce results if your agent is configured to
              send OpenTelemetry traces to Coval.{" "}
              <a
                href="https://docs.coval.ai/concepts/simulations/traces/opentelemetry#opentelemetry-traces"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
              >
                Learn how to set up traces →
              </a>
            </span>
          </Alert>
        </div>
      ) : null}

      <div className="min-h-0 flex-1">
        <Table<MetricPickerRowNode>
          groupingControls={
            <GroupingDisplay
              inline
              showExpansion
              value={grouping}
              onChange={handleGroupingChange}
              options={GROUPING_OPTIONS}
              allExpanded={allExpanded}
              onToggleExpanded={handleToggleExpandAll}
            />
          }
          aria-label="Select metrics"
          data={data}
          columns={columns}
          isExportRow={(row) => row.kind === "metric"}
          exportRowId={(row) =>
            row.kind === "metric"
              ? (row.metric.id ?? row.metric.metric_name)
              : row.id
          }
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          getRowId={(row) => row.id}
          getSubRows={(row) =>
            row.kind === "group" ? row.children : undefined
          }
          renderGroupRow={(row) => {
            if (row.kind !== "group") {
              return undefined
            }
            const GroupIcon = row.icon
            return (
              <>
                <GroupIcon className={iconClasses} />
                <span className="flex items-baseline gap-2">
                  <span className="text-lg font-semibold text-coval-text">
                    {row.group.label}
                  </span>
                  <span className="text-sm text-coval-text-muted">
                    {row.group.count}
                  </span>
                </span>
                {row.group.description ? (
                  <span className="hidden min-w-0 truncate text-coval-text-muted group-hover:block">
                    {row.group.description}
                  </span>
                ) : null}
              </>
            )
          }}
          // Column headers ride inside each section instead of a single header
          // at the top, and each one's select-all covers its own section.
          isColumnHeaderRow={(row) => row.kind === "columnHeader"}
          expandedIds={expandedIds}
          onExpandedChange={setExpandedGroups}
          selectable
          selectionVisibility="always"
          selectedIds={selectedRowIds}
          onSelectionChange={handleSelectionChange}
          // No select-all: a section's checkbox reads as picking metrics
          // wholesale, and its scope can only be the metrics this setup allows
          // — so it would report a section complete while the unavailable rows
          // in plain view below it stay unpicked. Metrics are chosen one at a
          // time, or by shift-clicking a range, which single-select has no use
          // for.
          showSelectAll={false}
          allowRangeSelection={allowMultipleSelection}
          isRowSelectable={(row) =>
            row.kind === "metric" && !metricErrors[row.metric.metric_name]
          }
          isRowSelectionDisabled={(row) =>
            row.kind === "metric" && isMetricAdditionBlocked(row.metric)
          }
          isRowClickable={(row) => row.kind === "metric"}
          // The row click picks the metric rather than navigating, so the
          // checkbox cell stays part of the target instead of opting out.
          rowClickSelects
          // No hover tint: every row here is a candidate, and the picked ones
          // are what the surface is for. The checkboxes are on show from the
          // start (`selectionVisibility`), so a tint would only compete with
          // the selected rows for the eye.
          rowHover={false}
          onRowClick={(row) => {
            if (row.kind !== "metric" || !isMetricSelectable(row.metric)) {
              return
            }
            handleMetricToggle(
              getSelectionKey(row.metric),
              !selectedNames.has(getSelectionKey(row.metric)),
            )
          }}
          onSortChange={(sort) =>
            setSortDescriptor(
              sort ?? {
                columnId: METRICS_SORT_OPTION.NAME,
                direction: "asc",
              },
            )
          }
          defaultSort={sortDescriptor}
          rowAttributes={(row) =>
            row.kind === "metric"
              ? {
                  "data-testid": `metric-option-${row.metric.id}`,
                  "data-metric-category": getMetricType(row.metric),
                  "data-metric-autoscore-eligible":
                    getMetricType(row.metric) !== TRACE_CATEGORY
                      ? "true"
                      : "false",
                }
              : {}
          }
          estimateRowHeight={(row, index) =>
            row.kind === "group"
              ? METRIC_GROUP_ROW_HEIGHT +
                (index === 0 ? 0 : METRIC_SECTION_GAP) +
                (expandedIds.includes(row.group.key)
                  ? METRIC_HEADER_CONTENT_GAP
                  : 0)
              : row.kind === "columnHeader"
                ? METRIC_COLUMN_HEADER_ROW_HEIGHT
                : METRIC_ROW_HEIGHT
          }
          cardRows
          // Platters sit flush with the section title's chevron rather than
          // indented under it, so they share the left margin of everything else
          // in this column.
          cardRowNestLead={0}
          edgeToEdge
          emptyState={
            searchQuery ? "No metrics match your search" : "No metrics found"
          }
          className="h-full bg-surface-contrast px-6 pb-6"
        />
      </div>
    </TableDisplayScope>
  )
}
